/**
 * CAGR utilities for calculator results.
 *
 * Kept dependency-free (no imports from client-flags, string-utils, etc.) so
 * calculators can call attachCAGR at the tail of calculate() without pulling
 * in the full shared_assets module graph — which at module-load time touches
 * window globals that don't exist in node test environments.
 *
 * Split out from calculator-input-builder.js during #5198 Phase 4.
 *
 * Flow:
 *   buildCalculatorInput() → computes cagrAnchors from raw events
 *                         → attaches to holdings
 *   calculator.calculate() → computes result
 *                         → calls attachCAGR(result, holdings.cagrAnchors, this.cagrOptions)
 *                         → returns result with .cagr + breakdown[].cagr
 */

/**
 * Compound Annual Growth Rate. Accepts either second- or millisecond-precision
 * timestamps; auto-detects via the 1e12 threshold.
 * @returns {number|null} Growth rate (e.g. 0.15 = 15%/yr) or null if inputs invalid.
 */
export function getCAGR(oldValue, newValue, oldTime, newTime) {
	const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60;
	if (oldTime > 1e12) oldTime = Math.floor(oldTime / 1000);
	if (newTime > 1e12) newTime = Math.floor(newTime / 1000);
	const yearsBetween = (newTime - oldTime) / SECONDS_PER_YEAR;
	if (oldValue <= 0 || yearsBetween <= 0) return null;
	return Math.pow(newValue / oldValue, 1 / yearsBetween) - 1;
}

/**
 * Compute CAGR anchor points (first matched event per class + portfolio-level
 * earliest/latest) from raw user + client events. Pure event matching, no
 * arithmetic on calculator results.
 *
 * @param {Object} userShares - userClient.shares (needs [k].events)
 * @param {Object} clientEquity - clientData.equity (needs [k].events)
 * @param {string[]} shareClasses
 * @returns {Object} { perClass: { [k]: { firstDate, firstValue } | null }, portfolio: { earliestDate, earliestValue, latestDate } | null }
 */
export function computeCAGRAnchors(userShares, clientEquity, shareClasses) {
	const perClass = {};
	let earliestDate = Infinity;
	let earliestValue = 0;
	let latestDate = 0;

	shareClasses.forEach(function (k) {
		const clEvents = clientEquity?.[k]?.events || [];
		const usEvents = userShares?.[k]?.events || [];
		if (!usEvents.length || !clEvents.length) {
			perClass[k] = null;
			return;
		}

		const matched = [];
		usEvents.forEach(function (ue) {
			const ce = clEvents.find(function (c) {
				return c.nrid === ue.nrid;
			});
			if (ce && ce.date) {
				matched.push({ date: Number(ce.date), grossDollars: Number(ue.grossDollars) || 0 });
			}
		});

		// Portfolio-level anchor updates first — must include events from all
		// classes, including those with only one matched event. The original
		// computeCAGR() tracked earliest/latest across every event in a
		// separate pass; consolidating into one loop dropped single-event
		// classes from the portfolio anchor. Restored here.
		matched.forEach(function (m) {
			if (m.date < earliestDate) {
				earliestDate = m.date;
				earliestValue = Math.abs(m.grossDollars);
			}
			if (m.date > latestDate) latestDate = m.date;
		});

		// Per-class anchor only when ≥2 matched events (original guard).
		if (matched.length < 2) {
			perClass[k] = null;
			return;
		}

		matched.sort(function (a, b) {
			return a.date - b.date;
		});
		perClass[k] = {
			firstDate: matched[0].date,
			firstValue: Math.abs(matched[0].grossDollars),
			// Track last matched event date so attachCAGR's non-current-time
			// fallback can use per-class latestDate (not firstDate) as endDate.
			latestDate: matched[matched.length - 1].date,
		};
	});

	return {
		perClass: perClass,
		portfolio:
			earliestDate < Infinity
				? {
						earliestDate: earliestDate,
						earliestValue: earliestValue,
						latestDate: latestDate,
					}
				: null,
	};
}

/**
 * Attach CAGR fields to a calculator result. Called by each calculator inside
 * calculate(), configured via the calculator's cagrOptions.
 *
 * @param {Object} result - Calculator result with breakdown[] and totalValue
 * @param {Object} cagrAnchors - Output of computeCAGRAnchors
 * @param {Object} [options]
 * @param {boolean} [options.excludeTransfers=false] - Subtract classData.transfers
 *   and result.totalTransfers before computing CAGR. Used by ContributionsAccruals
 *   and IncomeAllocations so received gifts (which inflate value but aren't
 *   investment growth) don't distort the growth rate.
 * @param {boolean} [options.useCurrentTimeForClassCagr=true]
 * @param {boolean} [options.useCurrentTimeForPortfolioCagr=true]
 * @param {number} [options.now] - "Current time" override (seconds), e.g. a report's As Of Date
 */
export function attachCAGR(result, cagrAnchors, options = {}) {
	if (!result?.breakdown?.length || !cagrAnchors) return;

	const useCurrentForClass = options.useCurrentTimeForClassCagr !== false;
	const useCurrentForPortfolio = options.useCurrentTimeForPortfolioCagr !== false;
	const excludeTransfers = options.excludeTransfers === true;
	const now = options.now ?? Math.floor(Date.now() / 1000);

	// Per-class
	result.breakdown.forEach(function (classData) {
		const anchor = cagrAnchors.perClass?.[classData.classKey];
		if (!anchor || anchor.firstValue <= 0) return;

		const transfers = excludeTransfers ? classData.transfers || 0 : 0;
		const endValue = (classData.value || 0) - transfers;
		if (endValue <= 0) return;

		const endDate = useCurrentForClass ? now : anchor.latestDate;
		classData.cagr = getCAGR(anchor.firstValue, endValue, anchor.firstDate, endDate);
	});

	// Portfolio
	if (!cagrAnchors.portfolio) return;
	const { earliestDate, earliestValue, latestDate } = cagrAnchors.portfolio;
	const totalTransfers = excludeTransfers ? result.totalTransfers || 0 : 0;
	const endTotalValue = (result.totalValue || 0) - totalTransfers;
	const startValue = result.totalContributions > 0 ? result.totalContributions : earliestValue;
	const endDate = useCurrentForPortfolio ? now : latestDate;
	if (endDate > earliestDate && startValue > 0 && endTotalValue > 0) {
		result.cagr = getCAGR(startValue, endTotalValue, earliestDate, endDate);
	}
}
